import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import styles from "./Button.module.styl";
import { Link } from "react-router-dom";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ReactComponent as ErrorIcon } from "./assets/error.svg";

function Content({ children, icon, onlyIcon }) {
    return (
        <div>
            {icon === "add" && (
                <div className={cx(styles.icon, onlyIcon && styles.icon_only)}>
                    <AddIcon />
                </div>
            )}
            {icon === "error" && (
                <div className={cx(styles.icon, onlyIcon && styles.icon_only)}>
                    <ErrorIcon />
                </div>
            )}
            <span className={styles.title}>{children}</span>
        </div>
    );
}

function Button(props) {
    const {
        children,
        color,
        size,
        outline,
        icon,
        isDisabled,
        fitToWidth,
        className,
        onClick,
        form,
        type,
        to,
        href,
        target,
        rel,
        testName,
    } = props;

    const onlyIcon = children == null;

    const classes = cx(
        styles.button,
        styles[`size_${size}`],
        onlyIcon && styles[`size_${size}_onlyIcon`],
        styles[`color_${color}`],
        outline && styles[`outline_color_${color}`],
        isDisabled && styles.disabled,
        fitToWidth && styles.fitToWidth,
        className,
    );

    if (to) {
        return (
            <Link
                to={to}
                className={classes}
                onClick={onClick}
                tabIndex={isDisabled ? -1 : undefined}
                data-test-name={testName}
            >
                <Content children={children} icon={icon} onlyIcon={onlyIcon} />
            </Link>
        );
    }

    if (href) {
        return (
            <a
                href={href}
                className={classes}
                target={target}
                rel={rel}
                onClick={onClick}
                tabIndex={isDisabled ? -1 : undefined}
                data-test-name={testName}
                onMouseDown={(event) => {
                    event.preventDefault();
                }}
            >
                <Content>{children}</Content>
            </a>
        );
    }

    return (
        <button
            className={classes}
            disabled={!!isDisabled}
            form={form}
            type={type}
            onClick={onClick}
            data-test-name={testName}
            onMouseDown={(event) => {
                event.preventDefault();
            }}
        >
            <Content children={children} icon={icon} onlyIcon={onlyIcon} />
        </button>
    );
}

Button.propTypes = {
    color: PropTypes.oneOf(["blue", "red", "gray"]),

    outline: PropTypes.bool,

    size: PropTypes.oneOf(["big", "small"]),

    icon: PropTypes.oneOf([null, "add", "error"]),

    /** Set if the button is disabled. */
    isDisabled: PropTypes.bool,

    /** When set, button will fit parent's width. */
    fitToWidth: PropTypes.bool,

    /** Add a classname to the button. */
    className: PropTypes.string,

    /** Handler to be called on click. */
    onClick: PropTypes.func,

    /** Set connected form id **/
    form: PropTypes.string,

    /** Set whether it is a button or a form submission */
    type: PropTypes.oneOf(["button", "reset", "submit"]),

    /** Provides a route for buttons being used as an internal link (uses 'react-router'-s Link). */
    to: PropTypes.string,

    /** Provides a url for buttons being used as a link. */
    href: PropTypes.string,

    /** Provides 'target' attribute for a link (used with 'href' only) */
    target: PropTypes.oneOf(["_self", "_blank"]),

    /** Provides 'rel' attribute for a link (used with 'href' only) */
    rel: PropTypes.string,

    testName: PropTypes.string,
};

Button.defaultProps = {
    color: "blue",
    outline: false,
    size: "big",
    icon: null,
    isDisabled: false,
    fitToWidth: false,
    className: null,
    onClick: null,
    form: null,
    type: "button",
    to: null,
    href: null,
    target: "_self",
    rel: "",
};

export default Button;
